import { cva } from 'class-variance-authority';

import { cn } from '@heidi/utils';

import { focusRing } from '../styles/focus-ring';

import type { ChipColor, ChipVariant } from './chip.types';

type ColorVariantFlavor = {
  base: string;
  disabled: string;
  interactive: string;
};

/**
 * Per-color × per-variant token bundles. Each (color, variant) pair holds three
 * flavors:
 * - `base`        — enabled bg + text at rest
 * - `disabled`    — bg + text when `disabled` is true (50% alpha tokens for
 *   entity colors)
 * - `interactive` — hover/active/`data-[state=open]` overlay applied only when
 *   the chip body is a button-like element (and not disabled). The
 *   `data-[state=open]` variant mirrors `active:` and engages while a Radix
 *   popup trigger is open.
 *
 * `default` variant: solid muted bg + foreground text; hover/active dim the
 * muted token via opacity.
 *
 * `ghost` variant: transparent bg + primary text at rest. Hover/active flip
 * to the entity-{color}-muted bg + entity-{color}-foreground text — the
 * color drives the interactive surface.
 *
 * `empty` variant: like `ghost` (transparent fill, no border) but with
 * `text-text-secondary` at rest instead of primary, so an unfilled slot reads
 * quieter than a ghost chip while staying color-agnostic. The entity color
 * only surfaces on hover / active / open, where the chip previews the filled
 * state by flipping the bg and text to the entity-{color} tokens. This keeps a
 * row of unselected slots visually quiet while still letting each slot reveal
 * its destination color on interaction.
 */
const colorTokens = {
  neutral: {
    default: {
      base: 'bg-entity-neutral-muted text-entity-neutral-foreground',
      disabled:
        'bg-entity-neutral-disabled-muted text-entity-neutral-disabled-foreground',
      interactive:
        'hover:bg-entity-neutral-muted/70 active:bg-entity-neutral-muted/90 data-[state=open]:bg-entity-neutral-muted/90',
    },
    ghost: {
      base: 'bg-transparent text-text-primary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-neutral-muted hover:text-entity-neutral-foreground active:bg-entity-neutral-muted active:text-entity-neutral-foreground data-[state=open]:bg-entity-neutral-muted data-[state=open]:text-entity-neutral-foreground',
    },
    // All empty variants share the same idle tone — transparent fill with
    // secondary text and no border — so an "unassigned" slot reads
    // consistently regardless of which color will eventually occupy it. It
    // mirrors the `ghost` variant except the resting text is `text-text-secondary`
    // (one step quieter than ghost's primary). On hover / active / open the
    // chip previews the filled state via the same overlay as ghost
    // (`entity-{color}-muted` background + `entity-{color}-foreground` text).
    empty: {
      base: 'bg-transparent text-text-secondary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-neutral-muted hover:text-entity-neutral-foreground active:bg-entity-neutral-muted active:text-entity-neutral-foreground data-[state=open]:bg-entity-neutral-muted data-[state=open]:text-entity-neutral-foreground',
    },
  },
  red: {
    default: {
      base: 'bg-entity-red-muted text-entity-red-foreground',
      disabled: 'bg-entity-red-disabled-muted text-entity-red-disabled-foreground',
      interactive:
        'hover:bg-entity-red-muted/70 active:bg-entity-red-muted/90 data-[state=open]:bg-entity-red-muted/90',
    },
    ghost: {
      base: 'bg-transparent text-text-primary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-red-muted hover:text-entity-red-foreground active:bg-entity-red-muted active:text-entity-red-foreground data-[state=open]:bg-entity-red-muted data-[state=open]:text-entity-red-foreground',
    },
    empty: {
      base: 'bg-transparent text-text-secondary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-red-muted hover:text-entity-red-foreground active:bg-entity-red-muted active:text-entity-red-foreground data-[state=open]:bg-entity-red-muted data-[state=open]:text-entity-red-foreground',
    },
  },
  orange: {
    default: {
      base: 'bg-entity-orange-muted text-entity-orange-foreground',
      disabled: 'bg-entity-orange-disabled-muted text-entity-orange-disabled-foreground',
      interactive:
        'hover:bg-entity-orange-muted/70 active:bg-entity-orange-muted/90 data-[state=open]:bg-entity-orange-muted/90',
    },
    ghost: {
      base: 'bg-transparent text-text-primary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-orange-muted hover:text-entity-orange-foreground active:bg-entity-orange-muted active:text-entity-orange-foreground data-[state=open]:bg-entity-orange-muted data-[state=open]:text-entity-orange-foreground',
    },
    empty: {
      base: 'bg-transparent text-text-secondary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-orange-muted hover:text-entity-orange-foreground active:bg-entity-orange-muted active:text-entity-orange-foreground data-[state=open]:bg-entity-orange-muted data-[state=open]:text-entity-orange-foreground',
    },
  },
  green: {
    default: {
      base: 'bg-entity-green-muted text-entity-green-foreground',
      disabled: 'bg-entity-green-disabled-muted text-entity-green-disabled-foreground',
      interactive:
        'hover:bg-entity-green-muted/70 active:bg-entity-green-muted/90 data-[state=open]:bg-entity-green-muted/90',
    },
    ghost: {
      base: 'bg-transparent text-text-primary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-green-muted hover:text-entity-green-foreground active:bg-entity-green-muted active:text-entity-green-foreground data-[state=open]:bg-entity-green-muted data-[state=open]:text-entity-green-foreground',
    },
    empty: {
      base: 'bg-transparent text-text-secondary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-green-muted hover:text-entity-green-foreground active:bg-entity-green-muted active:text-entity-green-foreground data-[state=open]:bg-entity-green-muted data-[state=open]:text-entity-green-foreground',
    },
  },
  blue: {
    default: {
      base: 'bg-entity-blue-muted text-entity-blue-foreground',
      disabled: 'bg-entity-blue-disabled-muted text-entity-blue-disabled-foreground',
      interactive:
        'hover:bg-entity-blue-muted/70 active:bg-entity-blue-muted/90 data-[state=open]:bg-entity-blue-muted/90',
    },
    ghost: {
      base: 'bg-transparent text-text-primary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-blue-muted hover:text-entity-blue-foreground active:bg-entity-blue-muted active:text-entity-blue-foreground data-[state=open]:bg-entity-blue-muted data-[state=open]:text-entity-blue-foreground',
    },
    empty: {
      base: 'bg-transparent text-text-secondary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-blue-muted hover:text-entity-blue-foreground active:bg-entity-blue-muted active:text-entity-blue-foreground data-[state=open]:bg-entity-blue-muted data-[state=open]:text-entity-blue-foreground',
    },
  },
  yellow: {
    default: {
      base: 'bg-entity-yellow-muted text-entity-yellow-foreground',
      disabled: 'bg-entity-yellow-disabled-muted text-entity-yellow-disabled-foreground',
      interactive:
        'hover:bg-entity-yellow-muted/70 active:bg-entity-yellow-muted/90 data-[state=open]:bg-entity-yellow-muted/90',
    },
    ghost: {
      base: 'bg-transparent text-text-primary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-yellow-muted hover:text-entity-yellow-foreground active:bg-entity-yellow-muted active:text-entity-yellow-foreground data-[state=open]:bg-entity-yellow-muted data-[state=open]:text-entity-yellow-foreground',
    },
    empty: {
      base: 'bg-transparent text-text-secondary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-yellow-muted hover:text-entity-yellow-foreground active:bg-entity-yellow-muted active:text-entity-yellow-foreground data-[state=open]:bg-entity-yellow-muted data-[state=open]:text-entity-yellow-foreground',
    },
  },
  purple: {
    default: {
      base: 'bg-entity-purple-muted text-entity-purple-foreground',
      disabled: 'bg-entity-purple-disabled-muted text-entity-purple-disabled-foreground',
      interactive:
        'hover:bg-entity-purple-muted/70 active:bg-entity-purple-muted/90 data-[state=open]:bg-entity-purple-muted/90',
    },
    ghost: {
      base: 'bg-transparent text-text-primary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-purple-muted hover:text-entity-purple-foreground active:bg-entity-purple-muted active:text-entity-purple-foreground data-[state=open]:bg-entity-purple-muted data-[state=open]:text-entity-purple-foreground',
    },
    empty: {
      base: 'bg-transparent text-text-secondary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-purple-muted hover:text-entity-purple-foreground active:bg-entity-purple-muted active:text-entity-purple-foreground data-[state=open]:bg-entity-purple-muted data-[state=open]:text-entity-purple-foreground',
    },
  },
  pink: {
    default: {
      base: 'bg-entity-pink-muted text-entity-pink-foreground',
      disabled: 'bg-entity-pink-disabled-muted text-entity-pink-disabled-foreground',
      interactive:
        'hover:bg-entity-pink-muted/70 active:bg-entity-pink-muted/90 data-[state=open]:bg-entity-pink-muted/90',
    },
    ghost: {
      base: 'bg-transparent text-text-primary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-pink-muted hover:text-entity-pink-foreground active:bg-entity-pink-muted active:text-entity-pink-foreground data-[state=open]:bg-entity-pink-muted data-[state=open]:text-entity-pink-foreground',
    },
    empty: {
      base: 'bg-transparent text-text-secondary',
      disabled: 'bg-transparent text-text-disabled',
      interactive:
        'hover:bg-entity-pink-muted hover:text-entity-pink-foreground active:bg-entity-pink-muted active:text-entity-pink-foreground data-[state=open]:bg-entity-pink-muted data-[state=open]:text-entity-pink-foreground',
    },
  },
} as const satisfies Record<ChipColor, Record<ChipVariant, ColorVariantFlavor>>;

const allColors = Object.keys(colorTokens) as ChipColor[];
const allVariants = [
  'default',
  'ghost',
  'empty',
] as const satisfies readonly ChipVariant[];

/**
 * `color → default-variant base` map. Retained for callers that need to
 * enumerate the available colors (e.g. Storybook control options).
 */
const colorClasses = Object.fromEntries(
  allColors.map((c) => [c, colorTokens[c].default.base])
) as Record<ChipColor, string>;

// Build the (color × variant) compound entries programmatically — three passes
// (base, disabled, interactive) keep the rule ordering intentional and let
// tailwind-merge resolve the cascade on `bg-*` / `text-*` conflicts.
const baseCompoundVariants = allColors.flatMap((color) =>
  allVariants.map((variant) => ({
    color,
    variant,
    className: colorTokens[color][variant].base,
  }))
);

const disabledCompoundVariants = allColors.flatMap((color) =>
  allVariants.map((variant) => ({
    color,
    variant,
    disabled: true as const,
    className: colorTokens[color][variant].disabled,
  }))
);

const interactiveCompoundVariants = allColors.flatMap((color) =>
  allVariants.map((variant) => ({
    color,
    variant,
    interactive: true as const,
    disabled: false as const,
    className: colorTokens[color][variant].interactive,
  }))
);

const colorAxis = Object.fromEntries(allColors.map((c) => [c, ''])) as Record<
  ChipColor,
  string
>;

/**
 * Variants for the chip body element (the part that holds the label, leading
 * icon, and trailing icon / chevron).
 */
const chipBodyVariants = cva(
  cn(
    // `whitespace-nowrap`: a chip's height is locked to `h-6`, so a wrapped
    // label would spill past the pill (and its dashed `empty` border) whenever
    // the chip is squeezed in a narrow flex row. Pills are single-line by design.
    'inline-flex h-6 select-none items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-sm font-normal leading-6 transition-colors',
    // Asymmetric padding — when an icon sits on either side, the chip's
    // padding on that side pulls inward to `1.5` (6px) so the icon's
    // visual weight balances the label without making the pill feel
    // crowded.
    'has-[[data-slot=chip-leading-icon]]:ps-1.5 has-[[data-slot=chip-trailing-icon]]:pe-1.5',
    "[&_svg:not([class*='size-'])]:size-3.5 [&_svg]:shrink-0"
  ),
  {
    variants: {
      color: colorAxis,
      variant: { default: '', ghost: '', empty: '' },
      disabled: { true: '', false: '' },
      interactive: {
        true: cn(
          'cursor-pointer',
          focusRing,
          'disabled:pointer-events-none disabled:cursor-not-allowed'
        ),
        false: '',
      },
    },
    compoundVariants: [
      ...baseCompoundVariants,
      ...interactiveCompoundVariants,
      // Disabled overrides land last so tailwind-merge wins on conflicting
      // bg/text classes regardless of the interactive flag.
      ...disabledCompoundVariants,
    ],
    defaultVariants: {
      color: 'neutral',
      variant: 'default',
      disabled: false,
      interactive: false,
    },
  }
);

/**
 * Wrapper used when the chip has both a body and a sibling dismiss button.
 * Owns the pill chrome (background, rounded shape) so the body can render as a
 * borderless interactive button.
 */
const chipWrapperVariants = cva(
  cn(
    // See `chipBodyVariants` — `whitespace-nowrap` keeps the label single-line
    // so it never spills the fixed `h-6` pill.
    'inline-flex h-6 select-none items-center gap-0.5 whitespace-nowrap rounded-full text-sm font-normal leading-6 transition-colors',
    "[&_svg:not([class*='size-'])]:size-3.5 [&_svg]:shrink-0"
  ),
  {
    variants: {
      color: colorAxis,
      variant: { default: '', ghost: '', empty: '' },
      disabled: { true: '', false: '' },
    },
    compoundVariants: [...baseCompoundVariants, ...disabledCompoundVariants],
    defaultVariants: {
      color: 'neutral',
      variant: 'default',
      disabled: false,
    },
  }
);

/**
 * Body classes used inside a wrapper (dismissible chip). Strips the pill
 * chrome — that lives on the wrapper — but keeps text/icon styling.
 */
const chipInnerBodyVariants = cva(
  cn(
    'inline-flex h-full items-center gap-1.5 rounded-full bg-transparent pe-1 ps-2 outline-none transition-colors',
    // Mirrors the body's leading-icon pull-in — when a leading icon is
    // present, tighten the start padding to `1.5` so the icon sits closer
    // to the chip's start curve.
    'has-[[data-slot=chip-leading-icon]]:ps-1.5'
  ),
  {
    variants: {
      interactive: {
        true: cn(
          'cursor-pointer',
          focusRing,
          // Palette-agnostic hover overlay tinted by the chip's foreground color
          // — mirrors the dismiss button's affordance so both interactive zones
          // surface a consistent click feedback.
          'hover:bg-current/15 active:bg-current/25',
          'disabled:cursor-not-allowed disabled:hover:bg-transparent'
        ),
        false: '',
      },
    },
    defaultVariants: { interactive: false },
  }
);

/**
 * Dismiss button styling — palette-agnostic so the X reads consistently
 * across all chip colors. The button hit area is 24×24 (WCAG 2.5.5 target-size
 * minimum) while the X glyph itself stays at size-3 (12px). On hover the button
 * fills with `bg-current/15` — a ghost-style overlay tinted by the chip's
 * foreground color — to surface the click zone without altering the icon.
 */
const chipDismissClasses = cn(
  // `-ms-1` pulls the dismiss circle toward the chip body so the X icon
  // sits closer to the label without overlapping the hit area.
  '-ms-1 inline-flex size-6 shrink-0 items-center justify-center rounded-full',
  'text-current opacity-70 transition-[background-color,opacity]',
  'hover:bg-current/15 hover:opacity-100',
  'active:bg-current/25',
  'focus-visible:opacity-100',
  focusRing,
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:opacity-50',
  '[&_svg]:size-3 [&_svg]:shrink-0'
);

export {
  chipBodyVariants,
  chipDismissClasses,
  chipInnerBodyVariants,
  chipWrapperVariants,
  colorClasses,
  colorTokens,
};
